import { SOLID } from "../collision/flags.ts";
import { leastCover } from "../movement/true-point.ts";
import { COLLISION_CELL, FOOT_HALF_WIDTH, type CollisionGrid } from "../movement/step.ts";
import { boxDistanceSquared } from "./shapes.ts";
import { lastClearCell } from "./sight.ts";

/**
 * A projectile's geometry (ADR-095 decisions 2, 5 and 7): where a shot lands, how long it flies, how long a standing
 * character takes to walk out of its burst, and whether the burst takes a feet box. Pure, integer results, and
 * allocation-free: a caller hands the landing a two-slot buffer it keeps.
 */

/**
 * The longest a shot flies at full range, in ticks (1.6 s).
 */
export const MAX_FLIGHT_TICKS = 32;

/**
 * The ticks the dodge check leaves a player to react, a quarter second.
 */
export const REACT_TICKS = 5;

/**
 * The farthest a burst reaches from its caster's origin: the range and the radius together, 9 tiles.
 */
export const MAX_PROJECTILE_REACH = 144;

/**
 * The ticks a standing character takes to walk out of a burst, whatever offsets its true point carries: the least
 * number of walking steps whose least cover (`leastCover`, the even distance rounded down) reaches the radius and half
 * a feet box (ADR-116 decision 28 and its amendment of 2026-10-07).
 *
 * @param radius The burst's radius in sixteenths.
 * @param walkSpeed The rules' `walk_speed`, in speed units, at least `SPEED_UNITS`.
 * @returns The ticks.
 * @example
 * walkOutTicks(24, 43);
 * // 11
 */
export function walkOutTicks(radius: number, walkSpeed: number): number {
  let ticks = 1;
  while (leastCover(walkSpeed, ticks) < radius + FOOT_HALF_WIDTH) {
    ticks += 1;
  }
  return ticks;
}

/**
 * The ticks a shot takes over a whole distance at a speed.
 *
 * @param distance The distance in sixteenths, a whole number of at least 0.
 * @param speed Sixteenths a tick, at least 1.
 * @returns `ceil(distance / speed)`.
 */
export function flightTicksAt(distance: number, speed: number): number {
  return Math.ceil(distance / speed);
}

/**
 * A launch's flight: the least whole number of ticks whose travel at the speed covers the straight line from the origin
 * to the landing point, compared in squared sixteenths so it is exact, and never below the least flight.
 *
 * @param originX The origin's x in sixteenths.
 * @param originY The origin's y.
 * @param landingX The landing point's x.
 * @param landingY The landing point's y.
 * @param speed Sixteenths a tick, at least 1.
 * @param minFlight The least flight in ticks (`projectile_min_flight`).
 * @returns The flight in ticks.
 */
export function projectileFlightTicks(originX: number, originY: number, landingX: number, landingY: number, speed: number, minFlight: number): number {
  const dx = landingX - originX;
  const dy = landingY - originY;
  const squared = dx * dx + dy * dy;
  let flight = Math.ceil(Math.sqrt(squared) / speed);
  while (flight > 0 && (flight - 1) * speed * ((flight - 1) * speed) >= squared) {
    flight -= 1;
  }
  while (flight * speed * (flight * speed) < squared) {
    flight += 1;
  }
  return Math.max(minFlight, flight);
}

/**
 * Where a shot lands (ADR-095 decision 5): the aim point, pulled in along the ray to the range when it lies beyond it
 * (each component truncated toward the origin, so the landing is never past the range), then pulled back to the centre of
 * the last cell before the first `SOLID` cell the ray meets, by the walk of `inSight` over the live grid. The grid's edge
 * is solid to the ray. Water and holes stop nothing. An origin whose own cell is solid lands on the origin.
 *
 * @param grid The live collision grid as it is at the release.
 * @param originX The origin's x in sixteenths, a whole number.
 * @param originY The origin's y.
 * @param aimX The aim point's x.
 * @param aimY The aim point's y.
 * @param range The range in sixteenths.
 * @param out Where the landing point goes: x at 0, y at 1; the cell the walk last cleared may also pass through it.
 */
export function projectileLanding(grid: CollisionGrid, originX: number, originY: number, aimX: number, aimY: number, range: number, out: Int32Array): void {
  let endX = aimX;
  let endY = aimY;
  const dx = aimX - originX;
  const dy = aimY - originY;
  const squared = dx * dx + dy * dy;
  if (squared > range * range) {
    const scale = range / Math.sqrt(squared);
    endX = originX + Math.trunc(dx * scale);
    endY = originY + Math.trunc(dy * scale);
  }
  if (lastClearCell(grid, originX, originY, endX, endY, out)) {
    out[0] = endX;
    out[1] = endY;
    return;
  }
  const column = out[0] as number;
  const row = out[1] as number;
  if (column === Math.floor(originX / COLLISION_CELL) && row === Math.floor(originY / COLLISION_CELL) && originSolid(grid, column, row)) {
    out[0] = originX;
    out[1] = originY;
    return;
  }
  out[0] = column * COLLISION_CELL + COLLISION_CELL / 2;
  out[1] = row * COLLISION_CELL + COLLISION_CELL / 2;
}

/**
 * Whether the origin's own cell is solid, the grid's edge solid.
 *
 * @param grid The grid.
 * @param column The cell's column.
 * @param row Its row.
 * @returns True when solid.
 */
function originSolid(grid: CollisionGrid, column: number, row: number): boolean {
  if (column < 0 || row < 0 || column >= grid.width || row >= grid.height) {
    return true;
  }
  return ((grid.cells[row * grid.width + column] as number) & SOLID) !== 0;
}

/**
 * Whether a burst takes a feet box (ADR-095 decision 7): the nearest point of the box lies within the radius of the
 * landing point, compared in squared sixteenths, edges included.
 *
 * @param landingX The landing point's x in sixteenths.
 * @param landingY The landing point's y.
 * @param radius The burst's radius in sixteenths.
 * @param targetX The target's feet x.
 * @param targetY The target's feet y.
 * @returns True when the burst takes it.
 */
export function burstHits(landingX: number, landingY: number, radius: number, targetX: number, targetY: number): boolean {
  return boxDistanceSquared(landingX, landingY, targetX, targetY) <= radius * radius;
}
